import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./CreatePassword.scss";
import Input from "../../Components/Common/Input/Input";
import Button from "../../Components/Common/Button/Button";
import { validateInviteToken, acceptInvite } from "../../services/userService";
import { PASSWORD_REGEX } from "../../utils/Common/Constant";
import OpenEye from "../../Assets/Img/OpenEye";
import CloseEye from "../../Assets/Img/CloseEye";

type TokenStatus = "validating" | "valid" | "invalid";

interface ApiResponse<T> {
  data: T | null;
  error: { message?: string } | null;
}

interface ValidateTokenData {
  success: boolean;
}

interface AcceptInviteData {
  success: boolean;
  message?: string;
}

export default function CreatePassword() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [status, setStatus] = useState<TokenStatus>("validating");
  const [tokenError, setTokenError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [apiError, setApiError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    (
      validateInviteToken(token) as Promise<ApiResponse<ValidateTokenData>>
    ).then(({ data, error }) => {
      if (error || !data?.success) {
        setTokenError(
          "This invite link has expired or is no longer valid. Please request a new invitation to continue.",
        );
        setStatus("invalid");
      } else {
        setStatus("valid");
      }
    });
  }, [token]);

  const validatePassword = (value: string): string | null => {
    if (!value) return "Password is required.";
    if (value.length < 12) return "Password must be at least 12 characters";
    if (value.length > 128) return "Password must be at most 128 characters.";
    if (!PASSWORD_REGEX.test(value))
      return "Password must contain at least one uppercase letter, one lowercase letter, and one number";
    return null;
  };

  const handlePasswordChange = (val: string): void => {
    setPassword(val);
    if (val) setPasswordError(validatePassword(val) ?? "");
    else setPasswordError("");
    if (confirmPassword)
      setConfirmError(val !== confirmPassword ? "Passwords do not match." : "");
  };

  const handleConfirmChange = (val: string): void => {
    setConfirmPassword(val);
    if (val) setConfirmError(val !== password ? "Passwords do not match." : "");
    else setConfirmError("");
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    setApiError("");
    if (!password) {
      setPasswordError("Password is required.");
      return;
    }
    if (passwordError) return;
    if (!confirmPassword) {
      setConfirmError("Please confirm your password.");
      return;
    }
    if (confirmError) return;
    setSubmitting(true);
    const { data, error: err } = (await acceptInvite(
      token!,
      password,
    )) as ApiResponse<AcceptInviteData>;
    setSubmitting(false);
    if (err || !data?.success) {
      setApiError(
        data?.message ??
          err?.message ??
          "Failed to create password. Please try again.",
      );
      return;
    }
    localStorage.clear();
    navigate("/", { replace: true });
  };

  if (status === "validating") {
    return (
      <div className="createPasswordScreen">
        <div className="cpStatusBox">
          <div className="cpSpinner" />
          <p className="cpStatusText">Validating your invite link...</p>
        </div>
      </div>
    );
  }

  if (status === "invalid") {
    return (
      <div className="createPasswordScreen">
        <div className="cpStatusBox">
          <h2>Link Invalid</h2>
          <p className="cpErrorText">{tokenError}</p>
          <Button
            className="createBtn cpStatusBtn"
            onClick={() => navigate("/", { replace: true })}
          >
            Back to Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="createPasswordScreen">
      <div className="container">
        <div className="page">
          <section className="hero">
            <div className="hero-copy">
              <h1>Secure Your Account</h1>
              <p>
                Choose a strong password to protect your CSC Construction
                account and continue.
              </p>
            </div>
          </section>

          <section className="createPanel">
            <h2>Create Password</h2>
            <p>Enter and confirm your new password below.</p>
            <form onSubmit={handleSubmit}>
              <div className="field">
                <label htmlFor="password">Password</label>
                <div className="pwWrap">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handlePasswordChange(e.target.value)
                    }
                    icon={
                      password ? (
                        !showPassword ? (
                          <CloseEye />
                        ) : (
                          <OpenEye />
                        )
                      ) : null
                    }
                    onIconClick={() => setShowPassword((v) => !v)}
                  />
                </div>
                {passwordError && <p className="fieldError">{passwordError}</p>}
              </div>
              <div className="field">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="pwWrap">
                  <Input
                    id="confirmPassword"
                    type={showConfirm ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleConfirmChange(e.target.value)
                    }
                    icon={
                      confirmPassword ? (
                        !showConfirm ? (
                          <CloseEye />
                        ) : (
                          <OpenEye />
                        )
                      ) : null
                    }
                    onIconClick={() => setShowConfirm((v) => !v)}
                  />
                </div>
                {confirmError && <p className="fieldError">{confirmError}</p>}
              </div>
              {apiError && <p className="formError">{apiError}</p>}
              <Button type="submit" className="createBtn" disabled={submitting}>
                {submitting ? "Creating..." : "Create Password"}
              </Button>
            </form>
            <div className="hint">
              Already have an account? <a href="/">Sign In</a>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
